/** Some important details
 *
 *  Don't forget to add noOfLines={1} to the text components inside each cell when defining the columns
 *
 *  To apply rowHoverProperties, pass them as a style object to the rowHoverProperties prop and add the
 * class .row-hover-effect to the row elements you want the properties to apply to
 */

import * as React from "react";
import { useRouter } from "next/navigation";
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  ColumnDef,
  RowData,
  OnChangeFn,
  SortingState,
  RowSelectionState,
  VisibilityState,
  getSortedRowModel,
} from "@tanstack/react-table";

declare module "@tanstack/react-table" {
  interface ColumnMeta<TData extends RowData, TValue> {
    width?: string | number;
    minWidth?: string | number;
    headerClassName?: string;
    cellClassName?: string;
    isSortable?: boolean;
    skipRowLink?: boolean;
    align?: "left" | "center" | "right";
    wrap?: boolean;
    noEllipsis?: boolean;
  }
}
import { useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import { NoxusFormLabel } from "../Form/NoxusFormLabel";
import {
  MoreVertical,
  MoveDown,
  MoveUp,
  RotateCcw,
  SearchX,
  Settings2,
  X,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import { genieMenu, IMenuEntry } from "../UI/NoxusMenu";
import { Button } from "../shadcn/button";
import { Checkbox } from "../shadcn/checkbox";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/shadcn/table";
import {
  TableControlsFooter,
  TableControlsFooterProps,
} from "./TableControlsFooter";
import {
  ColumnResizeHandle,
  ColumnResizeOverlay,
  computeScaledWidths,
  tradeWidths,
  useColumnDrag,
} from "./columnResize";
import { Skeleton } from "../shadcn/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { useFloating, offset, flip, shift } from "@floating-ui/react";
import { cn } from "@/lib/utils";
import { NoxusEmptyBlock } from "../UI/NoxusEmptyBlock";
import Link from "next/link";
import { usePermissions } from "@/hooks/usePermissions";
import { ConfirmationDialog } from "../Dialogs/ConfirmationDialog";
import { RowContextMenu } from "./RowContextMenu";
import { NoxusAlert } from "../UI/NoxusAlert";
import {
  BulkAction,
  BulkActionButtons,
  type PendingBulkAction,
} from "./BulkActionButtons";
import { createPortal } from "react-dom";
import { useGenieStageContainer } from "@/lib/genie";

export type { BulkAction };

const MIN_RESIZE_W = 60;
const NON_DATA_COLUMNS = new Set([
  "select-column",
  "menu-column",
  "customize-column",
]);

type DataTableProps<Data extends object> = {
  label?: string;
  labelTooltip?: string;
  data: Data[];
  columns: ColumnDef<Data, any>[];
  isLoading?: boolean;
  loadingRows?: number;
  rowOnClick?: (id: any, value: Data) => void;
  isRowClickable?: (value: Data) => boolean;
  isSortLoading?: boolean;
  noDataMessage?: string;
  emptyState?: React.ReactNode;
  onSort?: (sorting: SortingState) => void;
  // A sort set from outside the header (Genie's list tools); the header follows it.
  sorting?: SortingState;
  contextMenuItems?: (rowData: Data) => IMenuEntry[];
  permissionGroupId?: string;
  onContextMenuOpen?: (rowData: Data) => void;
  manualSorting?: boolean;
  noBorder?: boolean;
  noLines?: boolean;
  fixedHeader?: boolean;
  rowHref?: (rowData: Data) => string;
  rowClassName?: (rowData: Data) => string;
  rowTooltip?: (rowData: Data) => React.ReactNode | null | undefined;
  rowGrouping?: {
    getGroupId: (rowData: Data) => string;
    groups: { id: string; header: React.ReactNode; collapsed?: boolean }[];
  };
  tableLayout?: "auto" | "fixed";
  noShadow?: boolean;
  disableContextMenu?: boolean;
  compact?: boolean;
  noPrefetchLinks?: boolean;
  bulkActions?: BulkAction<Data>[];
  // Singular noun used in the "apply to all N …" confirmation (e.g. "run",
  // "conversation"). Defaults to "item".
  bulkResourceLabel?: string;
  rowDataAttributes?: (rowData: Data) => Record<string, string> | undefined;
  selectAllCount?: number;
  tableControls?: TableControlsFooterProps;
  resetSelectionKey?: unknown;
  // Column customization; pair with useTableViewPreferences for persistence.
  resizableColumns?: boolean;
  columnWidths?: Record<string, number>;
  onColumnWidthsChange?: (widths: Record<string, number>) => void;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  showColumnCustomizer?: boolean;
  // Restores the view's defaults (widths and visibility); wired to the gear
  // menu's "Reset table" item. Falls back to clearing internal state.
  onResetView?: () => void;
};

export function DataTable<Data extends object>({
  label,
  labelTooltip,
  data,
  columns,
  rowOnClick,
  isRowClickable,
  rowHref,
  noDataMessage = "No data to show",
  emptyState,
  onSort,
  sorting: sortingProp,
  noShadow = false,
  isSortLoading = false,
  isLoading = false,
  loadingRows = 5,
  contextMenuItems,
  permissionGroupId,
  onContextMenuOpen,
  manualSorting = true,
  noBorder = false,
  noLines = false,
  fixedHeader,
  tableLayout,
  disableContextMenu = false,
  compact = false,
  noPrefetchLinks = false,
  rowDataAttributes,
  bulkActions,
  bulkResourceLabel = "item",
  selectAllCount,
  tableControls,
  resetSelectionKey,
  rowClassName,
  rowTooltip,
  rowGrouping,
  resizableColumns = false,
  columnWidths: columnWidthsProp,
  onColumnWidthsChange,
  columnVisibility: columnVisibilityProp,
  onColumnVisibilityChange,
  showColumnCustomizer = false,
  onResetView,
}: /* isLoading,
  isEmpty,
  isError, */
DataTableProps<Data>) {
  const router = useRouter();
  const stageContainer = useGenieStageContainer();
  const { hasAnyPermission, hasAllPermissions } =
    usePermissions(permissionGroupId);
  // Controlled when the caller passes ``sorting``, else the table keeps its own.
  const [innerSorting, setInnerSorting] = useState<SortingState>([]);
  const sorting = sortingProp ?? innerSorting;
  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === "function" ? updater(sorting) : updater;
    if (sortingProp === undefined) setInnerSorting(next);
    onSort?.(next);
  };
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [isAllSelected, setIsAllSelected] = useState(false);
  const lastSelectedIndexRef = useRef<number | null>(null);
  const tableWrapperRef = React.useRef<HTMLDivElement>(null);

  const [colWidths, setColWidths] = useState<Record<string, number>>(
    columnWidthsProp ?? {}
  );
  const widthsRef = useRef(colWidths);
  widthsRef.current = colWidths;
  const onWidthsChangeRef = useRef(onColumnWidthsChange);
  onWidthsChangeRef.current = onColumnWidthsChange;

  const { resizingCol, beginDrag, isDragging } = useColumnDrag({
    enabled: resizableColumns,
    computeUpdates: ({ col, next, widths }, deltaX) =>
      next === undefined
        ? {}
        : tradeWidths({ col, next, widths, deltaX, min: MIN_RESIZE_W }),
    onFirstMove: ({ widths }) => {
      const merged = { ...widthsRef.current, ...widths };
      widthsRef.current = merged;
      setColWidths(merged);
    },
    onChange: (updates) => {
      const merged = { ...widthsRef.current, ...updates };
      widthsRef.current = merged;
      setColWidths(merged);
    },
    onDragEnd: () => onWidthsChangeRef.current?.(widthsRef.current),
  });

  useEffect(() => {
    if (columnWidthsProp && !isDragging()) setColWidths(columnWidthsProp);
  }, [columnWidthsProp, isDragging]);

  const [containerW, setContainerW] = useState(0);
  useLayoutEffect(() => {
    if (!resizableColumns) return;
    const el = tableWrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setContainerW(el.clientWidth));
    ro.observe(el);
    setContainerW(el.clientWidth);
    return () => ro.disconnect();
  }, [resizableColumns]);

  const [internalVisibility, setInternalVisibility] = useState<VisibilityState>(
    columnVisibilityProp ?? {}
  );
  const columnVisibility = columnVisibilityProp ?? internalVisibility;
  const handleColumnVisibilityChange = React.useCallback(
    (
      updater: VisibilityState | ((old: VisibilityState) => VisibilityState)
    ) => {
      const next =
        typeof updater === "function" ? updater(columnVisibility) : updater;
      setInternalVisibility(next);
      onColumnVisibilityChange?.(next);
    },
    [columnVisibility, onColumnVisibilityChange]
  );

  const beginResize = (colId: string, nextId: string, startX: number) => {
    const snapshot: Record<string, number> = {};
    tableWrapperRef.current
      ?.querySelectorAll<HTMLElement>("th[data-col-id]")
      .forEach((th) => {
        const id = th.dataset.colId;
        if (id && !NON_DATA_COLUMNS.has(id)) snapshot[id] = th.offsetWidth;
      });
    if (snapshot[colId] === undefined || snapshot[nextId] === undefined) return;
    beginDrag({ col: colId, next: nextId, startX, widths: snapshot });
  };

  const resetView = () => {
    widthsRef.current = {};
    setColWidths({});
    setInternalVisibility({});
    if (onResetView) {
      onResetView();
    } else {
      onColumnWidthsChange?.({});
      onColumnVisibilityChange?.({});
    }
  };

  const hideableColumns = useMemo(() => {
    if (!showColumnCustomizer) return [];
    return columns.flatMap((c) => {
      const id =
        c.id ??
        ("accessorKey" in c && typeof c.accessorKey === "string"
          ? c.accessorKey
          : undefined);
      if (!id) return [];
      const label =
        typeof c.header === "string" && c.header !== ""
          ? c.header
          : id.charAt(0).toUpperCase() + id.slice(1).replace(/[_-]+/g, " ");
      return [{ id, label }];
    });
  }, [columns, showColumnCustomizer]);

  useEffect(() => {
    setRowSelection({});
    setIsAllSelected(false);
  }, [resetSelectionKey]);
  const [pendingBulkAction, setPendingBulkAction] =
    useState<PendingBulkAction | null>(null);
  const [selectedRow, setSelectedRow] = useState<Data | null>(null);

  const isMenuItemVisible = React.useCallback(
    (item: IMenuEntry): boolean => {
      if (item.kind === "separator" || item.kind === "label") return true;
      if (item.isHidden) return false;
      if (!item.auth) return true;
      const { level, permissions, requiresAllPermissions = false } = item.auth;
      return requiresAllPermissions
        ? hasAllPermissions(permissions, level)
        : hasAnyPermission(permissions, level);
    },
    [hasAllPermissions, hasAnyPermission]
  );
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  // A modal dialog blocks pointer events outside it, so a menu for a table
  // inside one must render inside that dialog.
  const [menuRoot, setMenuRoot] = useState<HTMLElement | null>(null);
  const [isContextMenuJustClosed, setIsContextMenuJustClosed] = useState(false);

  const { refs, floatingStyles, update } = useFloating({
    strategy: "fixed",
    placement: "bottom-start",
    middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 })],
  });

  const filteredContextMenuItems = useMemo(() => {
    if (!contextMenuItems || !selectedRow) return [];
    return contextMenuItems(selectedRow).filter(isMenuItemVisible);
  }, [contextMenuItems, selectedRow, isMenuItemVisible]);

  const getFilteredMenuItems = React.useCallback(
    (rowData: Data) => {
      if (!contextMenuItems) return [];
      return contextMenuItems(rowData).filter(isMenuItemVisible);
    },
    [contextMenuItems, isMenuItemVisible]
  );

  const handleContextMenu = React.useCallback(
    (e: React.MouseEvent, rowData: Data) => {
      if (!contextMenuItems || disableContextMenu) return;
      const items = getFilteredMenuItems(rowData);
      if (items.length === 0) return;

      e.preventDefault();
      e.stopPropagation();

      const virtualRef = {
        getBoundingClientRect: () => ({
          width: 0,
          height: 0,
          x: e.clientX,
          y: e.clientY,
          top: e.clientY,
          left: e.clientX,
          right: e.clientX,
          bottom: e.clientY,
        }),
      };

      refs.setPositionReference(virtualRef);
      setMenuRoot(
        (e.currentTarget as HTMLElement).closest<HTMLElement>('[role="dialog"]')
      );
      setSelectedRow(rowData);
      setIsContextMenuOpen(true);
      onContextMenuOpen?.(rowData);
      queueMicrotask(() => update?.());
    },
    [
      contextMenuItems,
      getFilteredMenuItems,
      onContextMenuOpen,
      update,
      disableContextMenu,
      refs,
    ]
  );

  const handleMenuButtonClick = React.useCallback(
    (e: React.MouseEvent, rowData: Data) => {
      if (!contextMenuItems) return;
      const items = getFilteredMenuItems(rowData);
      if (items.length === 0) return;

      e.preventDefault();
      e.stopPropagation();

      const buttonEl = e.currentTarget as HTMLElement;
      const rect = buttonEl.getBoundingClientRect();
      const virtualRef = {
        getBoundingClientRect: () => ({
          width: rect.width,
          height: rect.height,
          x: rect.left,
          y: rect.top,
          top: rect.top,
          left: rect.left,
          right: rect.right,
          bottom: rect.bottom,
        }),
      };

      refs.setPositionReference(virtualRef);
      setMenuRoot(
        (e.currentTarget as HTMLElement).closest<HTMLElement>('[role="dialog"]')
      );
      setSelectedRow(rowData);
      setIsContextMenuOpen(true);
      onContextMenuOpen?.(rowData);
      queueMicrotask(() => update?.());
    },
    [contextMenuItems, getFilteredMenuItems, onContextMenuOpen, update, refs]
  );

  useEffect(() => {
    const handleClickOutside = () => {
      if (isContextMenuOpen) {
        setIsContextMenuJustClosed(true);
        setTimeout(() => {
          setIsContextMenuJustClosed(false);
        }, 500);
        setIsContextMenuOpen(false);
      }
    };

    const handleRightClickAnywhere = () => setIsContextMenuOpen(false);

    document.addEventListener("click", handleClickOutside);
    document.addEventListener("contextmenu", handleRightClickAnywhere, true);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener(
        "contextmenu",
        handleRightClickAnywhere,
        true
      );
    };
  }, [isContextMenuOpen]);

  useEffect(() => {
    if (isContextMenuOpen) update?.();
  }, [isContextMenuOpen, update]);

  const tableColumns = useMemo(() => {
    const cols: ColumnDef<Data, any>[] = [];

    if (bulkActions && !isLoading) {
      cols.push({
        id: "select-column",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected()
                ? true
                : table.getIsSomePageRowsSelected()
                  ? "indeterminate"
                  : false
            }
            className="pointer-events-none group-hover/select:border-primary"
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            className="pointer-events-none group-hover/select:border-primary"
            aria-label="Select row"
          />
        ),
        // "1%" would render unusably narrow under table-layout:fixed.
        meta: { width: resizableColumns ? 40 : "1%", skipRowLink: true },
      });
    }

    cols.push(...columns);

    if (contextMenuItems && !isLoading) {
      cols.push({
        id: "menu-column",
        header: "",
        cell: ({ row }) => {
          const items = getFilteredMenuItems(row.original);
          if (items.length === 0) return null;
          return (
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="iconsm"
                aria-label="Open menu"
                {...genieMenu(items)}
                onClick={(e) => handleMenuButtonClick(e, row.original)}
              >
                <MoreVertical className="h-4 w-4 text-muted-foreground!" />
              </Button>
            </div>
          );
        },
        meta: {
          width: "64px",
          cellClassName: "max-w-none",
        },
      });
    }

    if (showColumnCustomizer && !contextMenuItems && !isLoading) {
      cols.push({
        id: "customize-column",
        header: "",
        cell: () => null,
        meta: { width: "40px", skipRowLink: true },
      });
    }

    return cols;
  }, [
    columns,
    contextMenuItems,
    isLoading,
    handleMenuButtonClick,
    getFilteredMenuItems,
    bulkActions,
    resizableColumns,
    showColumnCustomizer,
  ]);

  const tableData = useMemo(
    () => (isLoading ? Array(loadingRows).fill({}) : data),
    [isLoading, data, loadingRows]
  );

  const loadingColumns = useMemo(
    () =>
      isLoading
        ? tableColumns.map((column) => ({
            ...column,
            cell: () =>
              column.header ? (
                <Skeleton
                  className={cn("w-full rounded", compact ? "h-5" : "h-5")}
                />
              ) : (
                <></>
              ),
          }))
        : tableColumns,
    [isLoading, tableColumns, compact]
  );

  // if (isLoading) {
  //   console.log(">>", tableData, loadingRows);
  // }

  const handleRowSelectionChange = React.useCallback(
    (
      updater:
        | RowSelectionState
        | ((old: RowSelectionState) => RowSelectionState)
    ) => {
      const newSelection =
        typeof updater === "function" ? updater(rowSelection) : updater;
      if (
        isAllSelected &&
        Object.keys(newSelection).length < Object.keys(rowSelection).length
      ) {
        setIsAllSelected(false);
      }
      setRowSelection(newSelection);
    },
    [isAllSelected, rowSelection]
  );

  const table = useReactTable({
    columns: loadingColumns,
    data: tableData,
    state: {
      sorting,
      rowSelection,
      columnVisibility,
    },
    enableRowSelection: !!bulkActions,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    manualSorting: manualSorting,
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    enableMultiSort: false,
    sortDescFirst: manualSorting ? undefined : true,
    onRowSelectionChange: handleRowSelectionChange,
    getRowId: React.useCallback((row: Data, index: number) => {
      if (Object.keys(row).includes("id")) {
        return (row as any).id;
      }
      return index;
    }, []),
  });

  const renderWidths = resizableColumns
    ? computeScaledWidths(
        table.getVisibleLeafColumns(),
        colWidths,
        containerW,
        NON_DATA_COLUMNS
      )
    : null;

  return (
    <>
      {label && <NoxusFormLabel label={label} tooltip={labelTooltip} />}

      <ColumnResizeOverlay active={resizingCol !== null} />

      <div
        ref={tableWrapperRef}
        className={cn(
          "bg-background w-full",
          fixedHeader && "max-h-full overflow-auto",
          !fixedHeader && "overflow-visible",
          noBorder ? "border-t" : "border rounded-md",
          noShadow && "shadow-none",
          // Clip so the one-frame rescale lag can't flash a scrollbar.
          resizableColumns &&
            "[&_[data-slot=table-container]]:overflow-x-hidden"
        )}
      >
        <Table
          className="overflow-y-visible"
          style={
            resizableColumns
              ? { tableLayout: "fixed" }
              : tableLayout
                ? { tableLayout }
                : undefined
          }
        >
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header, headerIndex) => {
                  const meta = header.column.columnDef.meta;

                  const sort = header.column.getIsSorted();

                  const isSelectColumn = header.column.id === "select-column";
                  const isFirstAfterSelect = bulkActions && headerIndex === 1;

                  const isCustomizerHost =
                    showColumnCustomizer &&
                    (header.column.id === "menu-column" ||
                      header.column.id === "customize-column");
                  const nextDataCol = resizableColumns
                    ? headerGroup.headers
                        .slice(headerIndex + 1)
                        .find((h) => !NON_DATA_COLUMNS.has(h.column.id))
                    : undefined;
                  const resizable =
                    resizableColumns &&
                    !NON_DATA_COLUMNS.has(header.column.id) &&
                    !!nextDataCol;

                  return (
                    <TableHead
                      key={header.id}
                      data-col-id={header.column.id}
                      onClick={
                        isSelectColumn
                          ? () =>
                              table.toggleAllPageRowsSelected(
                                !table.getIsAllPageRowsSelected()
                              )
                          : meta?.isSortable && !isSortLoading
                            ? header.column.getToggleSortingHandler()
                            : undefined
                      }
                      className={cn(
                        meta?.isSortable || isSelectColumn
                          ? "cursor-pointer"
                          : "cursor-default",
                        rowGrouping
                          ? "px-5 py-4"
                          : compact
                            ? "px-3 py-2"
                            : "px-4 py-2",
                        "bg-[#FCFCFC] rounded-t-md",
                        isFirstAfterSelect && "pl-0",
                        isSelectColumn && "group/select",
                        resizable && "relative",
                        meta?.headerClassName
                      )}
                      style={{
                        width: renderWidths
                          ? (renderWidths[header.column.id] ?? meta?.width)
                          : (colWidths[header.column.id] ?? meta?.width),
                        minWidth: meta?.minWidth,
                      }}
                    >
                      {isCustomizerHost ? (
                        <div className="flex justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="iconsm"
                                aria-label="Customize columns"
                                className="bg-transparent hover:bg-transparent opacity-50 hover:opacity-100"
                              >
                                <Settings2 className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              {hideableColumns.map((col) => {
                                const visible =
                                  columnVisibility[col.id] !== false;
                                const isLastVisible =
                                  visible &&
                                  hideableColumns.filter(
                                    (c) => columnVisibility[c.id] !== false
                                  ).length === 1;
                                return (
                                  <DropdownMenuCheckboxItem
                                    key={col.id}
                                    checked={visible}
                                    disabled={isLastVisible}
                                    onCheckedChange={(checked) =>
                                      handleColumnVisibilityChange({
                                        ...columnVisibility,
                                        [col.id]: checked,
                                      })
                                    }
                                    onSelect={(e) => e.preventDefault()}
                                  >
                                    {col.label}
                                  </DropdownMenuCheckboxItem>
                                );
                              })}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={resetView}>
                                <RotateCcw className="size-4" /> Reset table
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          {typeof header.column.columnDef.header ===
                          "string" ? (
                            <p
                              className={cn(
                                "justify-center text-muted-foreground text-sm font-medium font-['Inter'] leading-tight select-none",
                                resizableColumns && "min-w-0 truncate"
                              )}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </p>
                          ) : (
                            flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )
                          )}

                          {meta?.isSortable && (
                            <div className="flex ml-1 relative">
                              <MoveUp
                                aria-label="Sorted ascending"
                                color={sort === "asc" ? "#132532" : "#90959D"}
                                size="1rem"
                              />

                              <div className="absolute -right-2">
                                <MoveDown
                                  aria-label="Sorted descending"
                                  color={
                                    sort === "desc" ? "#132532" : "#90959D"
                                  }
                                  size="1rem"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {resizable && nextDataCol && (
                        <ColumnResizeHandle
                          label={`Resize ${header.column.id} column`}
                          isResizing={resizingCol === header.column.id}
                          onDragStart={(startX) =>
                            beginResize(
                              header.column.id,
                              nextDataCol.column.id,
                              startX
                            )
                          }
                        />
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {(rowGrouping
              ? [
                  ...rowGrouping.groups.flatMap((group) => [
                    { kind: "group" as const, group },
                    ...(group.collapsed
                      ? []
                      : table
                          .getRowModel()
                          .rows.filter(
                            (row) =>
                              rowGrouping.getGroupId(row.original) === group.id
                          )
                          .map((row) => ({ kind: "row" as const, row }))),
                  ]),
                  // Group metadata and page data refresh independently. Keep
                  // newly moved installations visible until both catch up.
                  ...table
                    .getRowModel()
                    .rows.filter(
                      (row) =>
                        !rowGrouping.groups.some(
                          (group) =>
                            group.id === rowGrouping.getGroupId(row.original)
                        )
                    )
                    .map((row) => ({ kind: "row" as const, row })),
                ]
              : table
                  .getRowModel()
                  .rows.map((row) => ({ kind: "row" as const, row }))
            ).map((item) => {
              if (item.kind === "group")
                return (
                  <TableRow
                    key={`group-${item.group.id}`}
                    className="border-y bg-muted/60 hover:bg-muted/60"
                  >
                    <TableCell
                      colSpan={table.getVisibleLeafColumns().length}
                      className="px-5 py-3"
                    >
                      {item.group.header}
                    </TableCell>
                  </TableRow>
                );
              const row = item.row;
              // Loading placeholder rows are empty objects, not real data.
              const href =
                rowHref && !isLoading ? rowHref(row.original) : undefined;
              const handleRowClick = () => {
                if (isContextMenuOpen || isContextMenuJustClosed) {
                  return;
                }

                if (href) {
                  router.push(href);
                } else if (rowOnClick) {
                  rowOnClick(row.id, row.original);
                }
              };

              const clickable = isRowClickable
                ? isRowClickable(row.original)
                : true;

              const handleSelectMouseDown = (e: React.MouseEvent) => {
                if (e.shiftKey) e.preventDefault();
              };

              const handleSelectClick = (e: React.MouseEvent) => {
                e.stopPropagation();
                const rows = table.getRowModel().rows;
                const index = rows.findIndex(
                  (candidate) => candidate.id === row.id
                );
                if (e.shiftKey && lastSelectedIndexRef.current !== null) {
                  const from = Math.min(lastSelectedIndexRef.current, index);
                  const to = Math.max(lastSelectedIndexRef.current, index);
                  const update: RowSelectionState = { ...rowSelection };
                  for (let i = from; i <= to; i++) {
                    update[rows[i].id] = true;
                  }
                  setRowSelection(update);
                } else {
                  row.toggleSelected(!row.getIsSelected());
                }
                lastSelectedIndexRef.current = index;
              };

              const extraAttrs = rowDataAttributes
                ? rowDataAttributes(row.original)
                : undefined;

              const tooltipContent = rowTooltip?.(row.original);

              const tableRow = (
                <TableRow
                  key={row.id}
                  className={cn(
                    "h-12",
                    (rowOnClick || href) && clickable && "cursor-pointer",
                    noLines && "border-b-0",
                    rowClassName?.(row.original) ?? ""
                  )}
                  onClick={href ? undefined : handleRowClick}
                  onContextMenu={(e) => handleContextMenu(e, row.original)}
                  {...extraAttrs}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => {
                    const meta = cell.column.columnDef.meta;

                    const cellContentInner = flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    );

                    const isSelectColumn = cell.column.id === "select-column";
                    const isFirstAfterSelect = bulkActions && cellIndex === 1;

                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          href && !meta?.skipRowLink
                            ? "p-0"
                            : rowGrouping
                              ? "px-5 py-4"
                              : compact
                                ? "px-3 py-2"
                                : "px-4 py-2",
                          isSelectColumn && "cursor-pointer group/select",
                          isFirstAfterSelect && "pl-0",
                          meta?.noEllipsis &&
                            "overflow-hidden whitespace-nowrap text-clip",
                          meta?.cellClassName
                        )}
                        style={{
                          width: meta?.width,
                          minWidth: meta?.minWidth,
                        }}
                        onMouseDown={
                          isSelectColumn ? handleSelectMouseDown : undefined
                        }
                        onClick={isSelectColumn ? handleSelectClick : undefined}
                      >
                        {href && !meta?.skipRowLink ? (
                          noPrefetchLinks ? (
                            <HoverPrefetchLink
                              href={href}
                              className={cn(
                                "block w-full h-full",
                                rowGrouping
                                  ? "px-5 py-4"
                                  : compact
                                    ? "px-3 py-2"
                                    : "px-4 py-2",
                                resizableColumns &&
                                  (meta?.noEllipsis
                                    ? "overflow-hidden"
                                    : "truncate")
                              )}
                              onClick={handleRowClick}
                            >
                              {cellContentInner}
                            </HoverPrefetchLink>
                          ) : (
                            <Link
                              href={href}
                              className={cn(
                                "block w-full h-full",
                                rowGrouping
                                  ? "px-5 py-4"
                                  : compact
                                    ? "px-3 py-2"
                                    : "px-4 py-2",
                                resizableColumns &&
                                  (meta?.noEllipsis
                                    ? "overflow-hidden"
                                    : "truncate")
                              )}
                              onClick={handleRowClick}
                              prefetch={noPrefetchLinks ? false : undefined}
                            >
                              {cellContentInner}
                            </Link>
                          )
                        ) : (
                          cellContentInner
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );

              if (tooltipContent) {
                return (
                  <Tooltip key={row.id}>
                    <TooltipTrigger asChild>{tableRow}</TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      {tooltipContent}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return tableRow;
            })}
            {(!data || data.length === 0) &&
              !isLoading &&
              !rowGrouping?.groups.length && (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={table.getVisibleLeafColumns().length}
                    className="p-0 pb-4"
                  >
                    {emptyState || (
                      <NoxusEmptyBlock
                        title={noDataMessage}
                        icon={SearchX}
                        size="xs"
                      />
                    )}
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>

        {tableControls && data && data.length > 0 && (
          <div
            className={cn(
              "border-t px-4 py-2 bg-[#FCFCFC]",
              // The wrapper is rounded but overflow-visible, so the footer
              // must round its own bottom corners or it paints over them.
              !noBorder && "rounded-b-md"
            )}
          >
            <TableControlsFooter
              {...tableControls}
              className="flex justify-center items-center w-full relative"
            />
          </div>
        )}
      </div>

      {bulkActions &&
        Object.keys(rowSelection).length > 0 &&
        (() => {
          const bar = (
            <div
              className={cn(
                "bottom-5 left-1/2 -translate-x-1/2 z-50",
                stageContainer ? "absolute" : "fixed"
              )}
            >
              <div
                className={cn(
                  "flex items-center px-2.5 py-1.5 bg-background border rounded-xl shadow-lg",
                  bulkActions.length >= 4 ? "gap-3" : "gap-2"
                )}
              >
                <span className="text-sm font-medium text-foreground whitespace-nowrap">
                  {isAllSelected
                    ? `All ${selectAllCount} selected`
                    : `${Object.keys(rowSelection).length} selected`}
                </span>
                <button
                  className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  onClick={() => {
                    setRowSelection({});
                    setIsAllSelected(false);
                  }}
                  aria-label="Clear selection"
                >
                  <X size="1rem" />
                </button>
                {!isAllSelected &&
                  selectAllCount !== undefined &&
                  selectAllCount > Object.keys(rowSelection).length && (
                    <>
                      <div className="w-px h-4 bg-border" />
                      <button
                        className="text-sm font-medium hover:opacity-80 transition-colors whitespace-nowrap text-blue-500 cursor-pointer"
                        onClick={() => {
                          setIsAllSelected(true);
                          table.toggleAllPageRowsSelected(true);
                        }}
                      >
                        Select all {selectAllCount}
                      </button>
                    </>
                  )}
                <div className="w-px h-4 bg-border" />
                <BulkActionButtons
                  bulkActions={bulkActions}
                  selectedRows={
                    isAllSelected
                      ? []
                      : table.getSelectedRowModel().rows.map((r) => r.original)
                  }
                  isAllSelected={isAllSelected}
                  selectAllCount={selectAllCount}
                  resourceLabel={bulkResourceLabel}
                  setPendingAction={setPendingBulkAction}
                  setRowSelection={setRowSelection}
                  setIsAllSelected={setIsAllSelected}
                />
              </div>
            </div>
          );
          return stageContainer ? createPortal(bar, stageContainer) : bar;
        })()}

      {filteredContextMenuItems.length > 0 &&
        selectedRow &&
        isContextMenuOpen && (
          <RowContextMenu
            items={filteredContextMenuItems}
            setFloating={refs.setFloating}
            floatingStyles={floatingStyles as React.CSSProperties}
            root={menuRoot}
            onClose={() => setIsContextMenuOpen(false)}
          />
        )}

      {!!pendingBulkAction && (
        <ConfirmationDialog
          isOpen={!!pendingBulkAction}
          onOpenChange={(open) => {
            if (!open) setPendingBulkAction(null);
          }}
          title={pendingBulkAction.title}
          description={pendingBulkAction.description}
          confirmText={pendingBulkAction.confirmText}
          confirmVariant={
            pendingBulkAction.destructive ? "destructive" : undefined
          }
          onConfirm={() => {
            pendingBulkAction.run();
            setPendingBulkAction(null);
          }}
        >
          {pendingBulkAction.note && (
            <NoxusAlert
              variant="default"
              description={pendingBulkAction.note}
            />
          )}
        </ConfirmationDialog>
      )}
    </>
  );
}

export function HoverPrefetchLink({
  href,
  children,
  className,
  onClick,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  const [active, setActive] = useState(false);

  return (
    <Link
      href={href}
      prefetch={active ? null : false}
      onMouseEnter={() => setActive(true)}
      className={className}
      onClick={onClick}
    >
      {children}
    </Link>
  );
}
